/**
 * Progress page (brief §progress: `/progress`; pedagogy spec §3.3.5). Order: the evidence tiles
 * (topics started, solved on your own, solved with help, re-tests passed), "Ligger du rätt i
 * tid?" (P2-4), the course map with the per-chapter topic tables, "Svagaste ämnen" (P2-2,
 * `#svagaste`), the calibration chart, recurring slips, "coming back", then
 * "study next", the flashcards tile, the activity chart (with the streak) and exam history.
 * Everything comes from a single `GET /api/progress/overview` call — mastery, evidence states
 * and calibration are computed server-side (`shared/mastery.ts`, `shared/evidence.ts`); this
 * page only renders them (spec §4.7).
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useAuth } from "@client/app/auth";
import { BarChart3, Layers } from "lucide-react";
import { Badge, Card, EmptyState, PageHeader, Spinner, StatTile } from "@client/components/ui";
import { paperLabel } from "@client/features/exam/paper";
import { practiceDeepLink } from "@client/features/practice/deepLink";
import { useLang, useT, useTp } from "@client/i18n";
import { formatDate, formatNumber, formatPoints } from "@client/lib/format";
import { verdictTone } from "@client/lib/verdict";
import { ActivityChart } from "./ActivityChart";
import { CalibrationCard } from "./CalibrationCard";
import { ComingBackCard } from "./ComingBackCard";
import { CourseMap } from "./CourseMap";
import { PaceCard } from "./PaceCard";
import { SlipsCard } from "./SlipsCard";
import { WeakestCard } from "./WeakestCard";
import { weakestTopics } from "./weakest";
import { useProgressOverview, useTopicsAndChapters } from "./api";
import type { EvidenceSummary, ExamHistoryEntry, Recommendation } from "./types";

function RecommendationCard({ rec, topicLabel }: { rec: Recommendation; topicLabel: string }) {
  const t = useT();
  const navigate = useNavigate();

  function studyNow() {
    navigate(practiceDeepLink({ chapters: rec.link.chapters, topics: rec.link.topics }));
  }

  return (
    <Card compact>
      <p className="text-xs font-medium uppercase tracking-wide text-ink-500 dark:text-ink-400">
        {t("progress.chapters.chapter")} {rec.chapter}
      </p>
      <p className="mt-1 font-display text-base text-ink-900 dark:text-ink-50">{topicLabel}</p>
      <Badge tone="neutral" className="mt-2">
        {t(`progress.studyNext.reason.${rec.reason}`)}
      </Badge>
      <button
        type="button"
        onClick={studyNow}
        className="mt-4 inline-flex h-9 w-full items-center justify-center rounded-lg bg-accent-700 text-sm font-medium text-white transition-colors hover:bg-accent-800 dark:bg-accent-600 dark:hover:bg-accent-500"
      >
        {t("progress.studyNext.cta")}
      </button>
    </Card>
  );
}

function ExamRow({ entry }: { entry: ExamHistoryEntry }) {
  const t = useT();
  const { lang } = useLang();
  const label = paperLabel(entry, t) ?? t("progress.exams.kind.generated");
  return (
    <li className="flex items-center justify-between gap-3 border-b border-ink-100 py-2 last:border-0 dark:border-ink-900">
      <div>
        <p className="text-sm font-medium text-ink-800 dark:text-ink-100">{label}</p>
        <p className="text-xs text-ink-500 dark:text-ink-400">{formatDate(entry.date, lang)}</p>
      </div>
      <div className="flex items-center gap-2">
        {entry.totalScore !== null && (
          <span className="text-sm tabular-nums text-ink-700 dark:text-ink-200">
            {formatPoints(entry.totalScore, lang)}
          </span>
        )}
        {entry.verdict ? (
          <Badge tone={verdictTone(entry.verdict)}>{entry.verdict}</Badge>
        ) : (
          <Badge tone="neutral">{t("progress.exams.ungraded")}</Badge>
        )}
      </div>
    </li>
  );
}

/** The four evidence tiles (pedagogy spec §3.3.5 item 2). */
function EvidenceTiles({ summary }: { summary: EvidenceSummary }) {
  const t = useT();
  const { lang } = useLang();
  const n = (value: number) => formatNumber(value, lang);
  return (
    <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
      <StatTile
        label={t("progress.summary.started.label")}
        value={t("progress.summary.started.value", {
          started: n(summary.topicsStarted),
          total: n(summary.topicsEligible),
        })}
      />
      <StatTile
        label={t("progress.summary.unaided.label")}
        value={n(summary.solvedUnaided)}
        hint={t("progress.summary.unaided.hint")}
      />
      <StatTile
        label={t("progress.summary.assisted.label")}
        value={n(summary.solvedWithHelp)}
        hint={t("progress.summary.assisted.hint")}
      />
      <StatTile
        label={t("progress.summary.retests.label")}
        value={t("progress.summary.retests.value", {
          passed: n(summary.retestsPassed),
          taken: n(summary.retestsTaken),
        })}
        hint={t("progress.summary.retests.hint")}
      />
    </div>
  );
}

/** Remembers, per account on this device, that "Så räknas det" has been shown open once. */
const HOW_COUNTED_SEEN = "progress.howCounted.seen";

function firstVisit(userId: string | undefined): boolean {
  if (!userId) return false;
  try {
    const key = `${HOW_COUNTED_SEEN}:${userId}`;
    if (window.localStorage.getItem(key)) return false;
    window.localStorage.setItem(key, "1");
    return true;
  } catch {
    return false;
  }
}

/**
 * "Så räknas det": what a count on this page means, one native disclosure line. Open on the
 * first visit (P1-7: students did not find it collapsed), closed after that.
 */
function HowCounted() {
  const t = useT();
  const { user } = useAuth();
  const userId = user?.id;
  // Decided once, when the account is known (auth may still be loading on the first render).
  const [open, setOpen] = useState<boolean | null>(null);
  useEffect(() => {
    if (open === null && userId) setOpen(firstVisit(userId));
  }, [open, userId]);
  return (
    <details open={open === true} className="mb-8 text-sm text-ink-700 dark:text-ink-200">
      <summary className="cursor-pointer rounded font-medium text-ink-800 dark:text-ink-100">
        {t("progress.howCounted.title")}
      </summary>
      <p className="mt-2 max-w-prose">{t("progress.howCounted.body")}</p>
    </details>
  );
}

export function ProgressPage() {
  const t = useT();
  const tp = useTp();
  const { lang } = useLang();
  const overviewQuery = useProgressOverview();
  const topicsQuery = useTopicsAndChapters();

  if (overviewQuery.isLoading) {
    return (
      <div className="mx-auto max-w-4xl">
        <PageHeader title={t("progress.nav")} description={t("progress.description")} />
        <Spinner size="md" label={t("common.loading")} />
      </div>
    );
  }

  const overview = overviewQuery.data;
  if (!overview) {
    return (
      <div className="mx-auto max-w-4xl">
        <PageHeader title={t("progress.nav")} description={t("progress.description")} />
        <EmptyState title={t("common.error")} />
      </div>
    );
  }

  const topicNameById = new Map(
    (topicsQuery.data?.topics ?? []).map((topic) => [topic.id, topic.name]),
  );
  const topicLabel = (topicId: string): string => {
    const name = topicNameById.get(topicId);
    return name ? (name[lang] ?? name.sv) : topicId;
  };

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader title={t("progress.nav")} description={t("progress.description")} />

      <EvidenceTiles summary={overview.summary} />
      <HowCounted />

      <PaceCard chapters={overview.chapters} />

      <section className="mb-8">
        <CourseMap
          chapters={overview.chapters}
          chapterInfos={topicsQuery.data?.chapters ?? []}
          topics={topicsQuery.data?.topics ?? []}
        />
      </section>

      <section id="svagaste" className="mb-8 scroll-mt-20">
        <WeakestCard topics={weakestTopics(overview.chapters)} topicLabel={topicLabel} />
      </section>

      <section className="mb-8">
        <CalibrationCard calibration={overview.calibration} />
      </section>

      <section className="mb-8">
        <SlipsCard slips={overview.slips} topicLabel={topicLabel} />
      </section>

      <section className="mb-8">
        <ComingBackCard rows={overview.comingBack} topicLabel={topicLabel} />
      </section>

      <section className="mb-8">
        <h2 className="mb-3 font-display text-lg text-ink-900 dark:text-ink-50">
          {t("progress.studyNext.title")}
        </h2>
        {overview.recommendations.length === 0 ? (
          <p className="text-sm text-ink-600 dark:text-ink-400">{t("progress.studyNext.empty")}</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-3">
            {overview.recommendations.map((rec) => (
              <RecommendationCard
                key={rec.topicId}
                rec={rec}
                topicLabel={topicLabel(rec.topicId)}
              />
            ))}
          </div>
        )}
      </section>

      <section className="mb-8">
        <Link to="/flashcards" className="block rounded-xl focus-visible:outline-none">
          <StatTile
            icon={<Layers className="h-5 w-5" />}
            label={t("flashcards.nav")}
            value={tp("progress.flashcards.due", overview.flashcards.dueNow)}
            hint={t("progress.flashcards.reviewsLast7d", {
              count: overview.flashcards.reviewsLast7d,
            })}
          />
        </Link>
      </section>

      <section className="mb-8">
        <Card
          title={
            <span className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              {t("progress.activity.title")}
            </span>
          }
          actions={
            <p className="text-right text-xs text-ink-600 dark:text-ink-400">
              <span>{t("progress.stats.streak.label")}</span>{" "}
              <span className="font-medium tabular-nums text-ink-900 dark:text-ink-50">
                {tp("progress.stats.streak.value", overview.streak.current)}
              </span>
              <br />
              <span>{tp("progress.stats.streak.longest", overview.streak.longest)}</span>
            </p>
          }
          compact
        >
          <ActivityChart days={overview.activity} />
        </Card>
      </section>

      <section>
        <Card
          title={t("progress.exams.title")}
          actions={
            <p className="text-xs text-ink-600 dark:text-ink-400">
              <span>{t("progress.stats.exams.label")}</span>{" "}
              <span className="font-medium tabular-nums text-ink-900 dark:text-ink-50">
                {formatNumber(overview.exams.length, lang)}
              </span>
            </p>
          }
          compact
        >
          {overview.exams.length === 0 ? (
            <p className="text-sm text-ink-600 dark:text-ink-400">{t("progress.exams.empty")}</p>
          ) : (
            <ul>
              {overview.exams.map((entry) => (
                <ExamRow key={entry.id} entry={entry} />
              ))}
            </ul>
          )}
        </Card>
      </section>
    </div>
  );
}

export default ProgressPage;
